import { useNavigate } from "react-router-dom";
import { MenuTop } from '../../../components/menu/top/menu-top';
import IconButtonComponent from '../../../components/Icon/icon-button';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import '../styles/admin-page.css';
import { useCallback, useEffect, useState } from "react";
import { getPlanUsage, IRemainingUsage } from "../../../features/search/service/search-service";
import { getMe } from "../../../features/search/service/user-service";
import { CircularProgress } from "@mui/material";

export const AdminPage = () => {
    const navigate = useNavigate();
    const [planUsage, setPlanUsage] = useState<IRemainingUsage | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchPlanUsage = useCallback(async (companyId: string) => {
        try {
            const data = await getPlanUsage(companyId);
            setPlanUsage(data);
        } catch (error) {
            console.error('Error fetching plan usage:');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchUserData = useCallback(async () => {
        try {
            const userData = await getMe();

            if (userData && userData.companyId) {
                fetchPlanUsage(userData.companyId);
            } else {
                setLoading(false);
            }
            
        } catch (error) {
            console.error('Error fetching user data:');
            setLoading(false);
        }
    }, [fetchPlanUsage]);

    useEffect(() => {
        fetchUserData();
    }, [fetchUserData]);

    const getUsageColor = (percentage: number) => {
        if (percentage < 50) return '#4caf50';
        if (percentage < 80) return '#ff9800';
        return '#f44336';
    };

    const getPercentageValue = (value: any): number => {
        if (value === null || value === undefined) return 0;
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        return isNaN(numValue) ? 0 : numValue;
    };

    const hasValidUsageData = planUsage && 
                              planUsage.licenses && 
                              typeof planUsage.licenses.used === 'number' &&
                              typeof planUsage.licenses.total === 'number' &&
                              typeof planUsage.licenses.remaining === 'number';
    
    return (
        <div>
            <MenuTop />
            <div className="admin-page-container">
                <h1>Painel Administrativo</h1>
                
                {loading ? (
                    <div className="usage-card">
                        <CircularProgress size={40} sx={{ color: '#3a8d8e' }} />
                    </div>
                ) : hasValidUsageData ? (
                    <div className="usage-card">
                        <div className="usage-header">
                            <span className="usage-label">Uso de Licenças</span>
                        </div>
                        
                        <div className="usage-progress-container">
                            <div 
                                className="usage-progress-bar"
                                style={{
                                    width: `${getPercentageValue(planUsage.licenses.percentageUsed)}%`,
                                    backgroundColor: getUsageColor(getPercentageValue(planUsage.licenses.percentageUsed))
                                }}
                            />
                        </div>

                        <div className="usage-stats">
                            <div className="usage-stat">
                                <span className="stat-value">{planUsage.licenses.used}</span>
                                <span className="stat-label">Utilizadas</span>
                            </div>
                            <div className="usage-stat-divider" />
                            <div className="usage-stat">
                                <span className="stat-value">{planUsage.licenses.remaining}</span>
                                <span className="stat-label">Restantes</span>
                            </div>
                            <div className="usage-stat-divider" />
                            <div className="usage-stat">
                                <span className="stat-value">{planUsage.licenses.total}</span>
                                <span className="stat-label">Total</span>
                            </div>
                        </div>

                        <div className="usage-percentage">
                            {getPercentageValue(planUsage.licenses.percentageUsed).toFixed(1)}% utilizado
                        </div>
                    </div>
                ) : null}

                <div className="admin-buttons-container">
                    <IconButtonComponent
                        label="Listar Usuários"
                        icon={<PersonIcon />}
                        onClick={() => navigate('/admin-painel/users')}
                    />

                    <IconButtonComponent
                        label="Listar Configurações"
                        icon={<SettingsIcon />}
                        onClick={() => navigate('/admin-painel/list-configs')}
                    />

                    <IconButtonComponent
                        label="Novo Usuário"
                        icon={<PersonIcon />}
                        onClick={() => navigate('/admin-painel/add-user')}
                    />
                </div>
            </div>
        </div>
    );
}
